import 'twin.macro';
import React, { useEffect, useRef, useState } from 'react';
import Dialog from '@teetsh/app/src/components/Dialog';
import Button from '@teetsh/app/src/components/Buttons/Button';
import {
  ClassroomsMapping,
  ClassroomMapping,
  NO_COPY,
} from '@teetsh/app/src/components/EntitiesSelectorModals/ClassroomsMappingModal/ClassroomsMapping';
import {
  SchoolSelector,
  Option,
} from '@teetsh/app/src/components/EntitiesSelectorModals/SchoolSelector';
import { SchoolDatePicker } from '@teetsh/app/src/components/Form/DatePicker/SchoolDatePicker';
import { hasSimpleMapping } from '@teetsh/app/src/components/EntitiesSelectorModals/ClassroomsMappingModal/hasSimpleMappping';
import { Classroom } from '@teetsh/app/src/types/classroom';
import { Schoolyear } from '@teetsh/app/src/types/schoolyear';
import { UserLocale } from '@teetsh/app/src/types/user';
import { useTranslation } from 'react-i18next';

interface Props {
  isOpen: boolean;
  srcClassrooms: Classroom[];
  locale: UserLocale;
  onClose: () => void;
  onSubmit: ({
    schoolyearId,
    schoolId,
    classroomsMapping,
    date,
  }: {
    schoolId?: string;
    schoolyearId?: string;
    classroomsMapping?: ClassroomMapping[];
    date?: Date;
  }) => any;
  enableDateStep?: boolean;
  enableCreateClassroom?: boolean;
  schoolyear?: Schoolyear;
}

const AllSteps = ['school', 'mapping', 'date'] as const;
type Step = (typeof AllSteps)[number];

const Wording: {
  [K in Step]: {
    label: 'selectSchool' | 'selectDate' | 'mapClassroom';
  };
} = {
  school: {
    label: 'selectSchool',
  },
  date: {
    label: 'selectDate',
  },
  mapping: {
    label: 'mapClassroom',
  },
};

export function ClassroomsMappingModal(props: Props) {
  const {
    isOpen,
    srcClassrooms,
    onSubmit,
    onClose,
    enableDateStep = false,
    enableCreateClassroom = true,
    schoolyear,
  } = props;

  const [entitiesSelected, setEntitiesSelected] = useState<Option | undefined>(
    undefined,
  );
  const [classroomMapping, setClassroomMapping] = useState<ClassroomMapping[]>(
    [],
  );
  const { t } = useTranslation(['common']);

  useEffect(() => {
    setClassroomMapping(
      srcClassrooms.map((c, i) => ({
        from: c.id,
        to:
          (entitiesSelected?.classrooms || []).length > i
            ? entitiesSelected?.classrooms[i].id
            : NO_COPY,
      })),
    );
  }, [JSON.stringify(entitiesSelected), srcClassrooms]);

  const [step, setStep] = useState<Step>('school');
  const completeButtonRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    new Date(),
  );

  const hasMultipleMapping =
    (entitiesSelected?.classrooms || []).length > 1 || srcClassrooms.length > 1;

  const steps = AllSteps.filter(
    s =>
      (s === 'date' && enableDateStep) ||
      (s === 'mapping' && hasMultipleMapping) ||
      (s === 'school' && !schoolyear),
  );
  const prevStep = steps[steps.indexOf(step) - 1];
  const nextStep = steps[steps.indexOf(step) + 1];

  useEffect(() => {
    if (schoolyear) {
      if (!entitiesSelected) {
        setEntitiesSelected({
          schoolyear: schoolyear,
          school: schoolyear.schools[0],
          classrooms: schoolyear.schools[0].classrooms,
        });
      }
      if (step === 'school' && nextStep) {
        setStep(nextStep);
      }
    }
  }, [schoolyear, nextStep]);

  const onCloseWithReset = () => {
    setEntitiesSelected(undefined);
    setSelectedDate(undefined);
    setClassroomMapping([]);
    setStep(steps[0]);
    return onClose();
  };

  return (
    <Dialog
      size="md"
      isOpen={isOpen}
      title={t(`common:components.classroomMapping.${Wording[step].label}`)}
      onClose={onCloseWithReset}
      initialFocus={completeButtonRef}
    >
      <Dialog.Body>
        {step === 'school' && (
          <div tw="mb-4">
            <SchoolSelector
              value={entitiesSelected?.school?.id}
              onChange={({ classrooms, school, schoolyear: sy }) =>
                setEntitiesSelected({
                  classrooms,
                  school,
                  schoolyear: sy,
                })
              }
            />
          </div>
        )}
        {step === 'mapping' && (
          <div tw="mb-4 pb-32" data-testid="classroomMapping">
            <ClassroomsMapping
              srcClassrooms={srcClassrooms}
              destClassrooms={entitiesSelected?.classrooms || []}
              value={classroomMapping}
              onChange={setClassroomMapping}
              enableCreateClassroom={enableCreateClassroom}
            />
          </div>
        )}
        {step === 'date' && (
          <div tw="flex justify-center">
            <div data-testid="calendar" tw="p-2">
              <SchoolDatePicker
                userLocale={t('common:locale.long') as UserLocale}
                mode="single"
                calendarRef={completeButtonRef}
                classrooms={entitiesSelected?.classrooms}
                onChange={v => {
                  setSelectedDate(v as Date);
                }}
                value={selectedDate}
                sundayStart={false}
              />
            </div>
          </div>
        )}
      </Dialog.Body>
      <Dialog.Footer>
        <Button
          large
          minimal
          onClick={() => (prevStep ? setStep(prevStep) : onCloseWithReset())}
        >
          {prevStep ? t('common:buttons.previous') : t('common:buttons.cancel')}
        </Button>
        <Button
          large
          intent="primary"
          disabled={!entitiesSelected}
          onClick={() => {
            if (nextStep) {
              return setStep(nextStep);
            }

            // no more step, submit
            if (
              hasSimpleMapping(
                srcClassrooms,
                entitiesSelected?.classrooms || [],
              )
            ) {
              return onSubmit({
                schoolyearId: entitiesSelected?.schoolyear?.id,
                schoolId: entitiesSelected?.school?.id,
                classroomsMapping: [
                  {
                    from: srcClassrooms[0].id,
                    to: entitiesSelected?.classrooms[0].id,
                  },
                ],
                date: selectedDate,
              }).then(() => onCloseWithReset());
            }
            if (hasMultipleMapping) {
              return onSubmit({
                schoolyearId: entitiesSelected?.schoolyear?.id,
                schoolId: entitiesSelected?.school?.id,
                classroomsMapping: classroomMapping.filter(
                  c => c.to !== NO_COPY,
                ),
                date: selectedDate,
              }).then(() => onCloseWithReset());
            }
            return Promise.resolve();
          }}
        >
          {nextStep ? t('common:buttons.next') : t('common:buttons.copy')}
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
}
